import { useMemo } from "react";
import { useZeroSelector } from "../../hooks/use-zero-selector.js";
import { queries } from "../queries";
import { useCurrentUser } from "../current-user";
import { createIncrementalMapSelector } from "../incremental-map-selector";
import { useStableMap } from "../use-stable-map";

const MEMBERSHIPS_KEY = "channel-members.byUser";
const UCS_KEY = "user-channel-stats.byUser";
const SHARED_WS_KEY = "workspace-members.allShared";

// ─── Selector factories ─────────────────────────────────────────────
function createMutedSetSelectorClean() {
  let prev = new Set<string>();
  let prevKey: string | null = null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (data: any[]): Set<string> => {
    const muted = data
      .filter((m) => m.muted)
      .map((m) => m.channel_id as string);
    const key = muted.sort().join(",");
    if (key === prevKey) return prev;
    prev = new Set(muted);
    prevKey = key;
    return prev;
  };
}

// P4a (channel_members.last_read_at split): two-source selectors —
// last_read_at sourced from user_channel_stats; joined_at sourced from
// channel_members. Merged in `useLastReadMap` / `useLastReadAt`.
function createUcsLastReadMapSelector() {
  return createIncrementalMapSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (s: any) => s.channel_id as string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (s: any) => (s.last_read_at ?? null) as number | null,
    (prev, next) => prev === next,
  );
}

function createCmJoinedAtMapSelector() {
  return createIncrementalMapSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (m: any) => m.channel_id as string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (m: any) => (m.joined_at ?? null) as number | null,
    (prev, next) => prev === next,
  );
}

function createSharedWsUserIdSetSelector() {
  let prev = new Set<string>();
  let prevKey: string | null = null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (data: any[]): Set<string> => {
    const ids = data.map((m) => m.user_id as string);
    const key = ids.sort().join(",");
    if (key === prevKey) return prev;
    prev = new Set(ids);
    prevKey = key;
    return prev;
  };
}

// ─── Module-level singleton selectors + defaults ─────────────────────
const mutedSetSelector = createMutedSetSelectorClean();
const ucsLastReadMapSelector = createUcsLastReadMapSelector();
const cmJoinedAtMapSelector = createCmJoinedAtMapSelector();
const sharedWsUserIdSetSelector = createSharedWsUserIdSetSelector();
const EMPTY_MUTED_SET = new Set<string>();
const EMPTY_UCS_LAST_READ_MAP = new Map<string, number | null>();
const EMPTY_JOINED_AT_MAP = new Map<string, number | null>();
const EMPTY_WS_USER_IDS = new Set<string>();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const EMPTY_MEMBERSHIPS: any[] = [];
// Shallow copy — must create new reference on each call because Zero's IVM
// mutates arrays in place. Identity selector (data => data) would always return
// the same reference, making isEqual comparisons compare the object to itself.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const membershipsSelector = (data: readonly any[]) => [...data];

const ucsLastReadAtSelectorCache = new Map<
  string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (data: any[]) => number | null
>();

// ─── Hooks ──────────────────────────────────────────────────────────

/** Set of muted channel IDs. Stable unless mute state changes. */
export function useMutedSet() {
  const { user } = useCurrentUser();
  const selector = mutedSetSelector;
  return useZeroSelector(
    MEMBERSHIPS_KEY,
    user ? queries.channel_members.byUser() : null,
    selector,
    { defaultValue: EMPTY_MUTED_SET },
  );
}

/** Single channel's last-read timestamp. Falls back to `joined_at` so
 *  freshly-joined channels with no read pointer don't report null
 *  (which downstream consumers misinterpret as "everything unread").
 *
 *  P4a (channel_members.last_read_at split): last_read_at now sourced
 *  from `user_channel_stats`; `joined_at` still from `channel_members`.
 *  Reads from both. Eliminates the IVM cascade that fired on every
 *  cm.last_read_at write — readers no longer subscribe to that column. */
export function useLastReadAt(channelId: string) {
  const { user } = useCurrentUser();

  // ucs.last_read_at — single-channel selector (cached by channelId).
  let ucsSelector = ucsLastReadAtSelectorCache.get(channelId);
  if (!ucsSelector) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ucsSelector = (data: any[]) => {
      const s = data.find((item) => item.channel_id === channelId);
      return (s?.last_read_at ?? null) as number | null;
    };
    ucsLastReadAtSelectorCache.set(channelId, ucsSelector);
  }
  const lastReadAtFromUcs = useZeroSelector(
    UCS_KEY,
    user ? queries.user_channel_stats.byUser() : null,
    ucsSelector,
  );

  // cm.joined_at — full-map selector reused via the singleton.
  const joinedAtMap = useZeroSelector(
    MEMBERSHIPS_KEY,
    user ? queries.channel_members.byUser() : null,
    cmJoinedAtMapSelector,
    { defaultValue: EMPTY_JOINED_AT_MAP },
  );

  // useZeroSelector returns [value, status] tuple; preserve that shape.
  const [lastReadAtValue, ucsStatus] = lastReadAtFromUcs;
  const [joinedAtMapValue] = joinedAtMap;

  const merged = useMemo(
    () => lastReadAtValue ?? joinedAtMapValue.get(channelId) ?? null,
    [lastReadAtValue, joinedAtMapValue, channelId],
  );
  // Return tuple matching the original useZeroSelector shape. Use the
  // ucs status (the more-frequently-changing source) as the surfaced
  // status; consumers ignore status anyway per project convention.
  return useMemo(() => [merged, ucsStatus] as const, [merged, ucsStatus]);
}

/** Full last-read Map. Used by mark-channel-read + inbox feed.
 *
 *  P4a (channel_members.last_read_at split): merges
 *  `user_channel_stats.last_read_at` (current) with
 *  `channel_members.joined_at` (fallback for never-marked-read channels).
 *  Wrapped in `useStableMap` so identical merged contents preserve ref
 *  identity across emissions. */
export function useLastReadMap() {
  const { user } = useCurrentUser();

  const lastReadFromUcs = useZeroSelector(
    UCS_KEY,
    user ? queries.user_channel_stats.byUser() : null,
    ucsLastReadMapSelector,
    { defaultValue: EMPTY_UCS_LAST_READ_MAP },
  );
  const joinedAtFromCm = useZeroSelector(
    MEMBERSHIPS_KEY,
    user ? queries.channel_members.byUser() : null,
    cmJoinedAtMapSelector,
    { defaultValue: EMPTY_JOINED_AT_MAP },
  );

  const [ucsMap] = lastReadFromUcs;
  const [joinedAtMap] = joinedAtFromCm;

  // Merge: for every membership (joined_at), prefer ucs.last_read_at if
  // present, else fall back to joined_at. Iterating joinedAtMap (not
  // ucsMap) ensures we don't surface stats rows for memberships that
  // no longer exist (kicked / rejoined / etc).
  const merged = useMemo(() => {
    const result = new Map<string, number | null>();
    for (const [channelId, joinedAt] of joinedAtMap) {
      const lastRead = ucsMap.get(channelId);
      result.set(channelId, lastRead ?? joinedAt);
    }
    return result;
  }, [ucsMap, joinedAtMap]);

  const stableMerged = useStableMap(merged);

  // Match the original useZeroSelector tuple shape so callers using
  // `const [lastReadMap] = useLastReadMap()` keep working unchanged.
  // Use the ucs status (more frequently-changing source) for the
  // surfaced status; consumers ignore status anyway per convention.
  const [, ucsStatus] = lastReadFromUcs;
  return useMemo(
    () => [stableMerged, ucsStatus] as const,
    [stableMerged, ucsStatus],
  );
}

/** Full membership list. For channel access checks. */
export function useMyMemberships() {
  const { user } = useCurrentUser();
  const selector = membershipsSelector;
  return useZeroSelector(
    MEMBERSHIPS_KEY,
    user ? queries.channel_members.byUser() : null,
    selector,
    { defaultValue: EMPTY_MEMBERSHIPS },
  );
}

/** User IDs across all shared workspaces. For Home DM grouping. */
export function useSharedWorkspaceUserIdSet() {
  const { user } = useCurrentUser();
  const selector = sharedWsUserIdSetSelector;
  return useZeroSelector(
    SHARED_WS_KEY,
    user ? queries.workspace_members.allSharedWorkspaceMembers() : null,
    selector,
    { defaultValue: EMPTY_WS_USER_IDS },
  );
}
